/**
 * Cohort filtering for the monitoring pages (COHORT-SCHEDULING-PLAN
 * Phase 4, requirement 3): Live Sessions, Review Queue and Leadership
 * filter their existing assignment lists by `cohort_assignment_id` —
 * no new views, no new pages.
 *
 * The coordinator picks a COHORT; assignments are stamped with the
 * cohort × orientation PAIRING id, so the filter is "any of this
 * cohort's pairing ids" (a cohort can hold several orientations).
 */

type CohortLike = {
  cohortId: bigint;
  pairings: ReadonlyArray<{ cohortAssignmentId: bigint }>;
};

/**
 * The pairing-id set for the picked cohort, or null for "no filter".
 * String keys because bigint Set membership is by identity-safe value —
 * stringifying keeps comparisons robust across binding shapes.
 */
export function pairingIdsOf(
  cohorts: ReadonlyArray<CohortLike>,
  cohortId: bigint | null,
): Set<string> | null {
  if (cohortId == null) return null;
  const cohort = cohorts.find((c) => c.cohortId === cohortId);
  return new Set(
    (cohort?.pairings ?? []).map((p) => String(p.cohortAssignmentId)),
  );
}

/**
 * Does an assignment pass the filter? `null` filter passes everything;
 * an un-stamped assignment (individual, not cohort-created/adopted)
 * never matches a cohort filter.
 */
export function inCohortFilter(
  cohortAssignmentId: bigint | null | undefined,
  pairingIds: Set<string> | null,
): boolean {
  if (pairingIds == null) return true;
  if (cohortAssignmentId == null) return false;
  return pairingIds.has(String(cohortAssignmentId));
}
